import React, {useMemo, useRef} from "react";
import styles from "./styles.module.scss";
import {useIntersectionObserver} from "@hook";
import {IPage} from "@data";
import Loading from "../Loading";

interface IProps<T> {
  list?: T[];
  page?: IPage;
  renderItem: (value: T, index: number) => React.JSX.Element;
  containerClass?: string;
  loadingWidth?: number;
  getList?: (page: number) => void;
  isUsingLoading?: boolean;
  enabled?: boolean; // 무한로딩 허용 여부
  loadingRef?: React.RefObject<boolean>;
  isFullLoading?: boolean;
  emptyText?: string;
  emptyBtn?: {text: string; onClick: () => void};
}

export default function Component<T>({
  list,
  page,
  renderItem,
  getList,
  containerClass,
  isUsingLoading = true,
  loadingWidth = 35,
  enabled = true,
  loadingRef,
  isFullLoading = false,
  emptyText,
  emptyBtn,
}: IProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);
  const lastPageRef = useRef(0);
  const listData = useMemo(() => {
    if (typeof list !== "object") {
      return [];
    }
    if (!Array.isArray(list)) {
      return [];
    }
    return list;
  }, [list]);

  const isLoadingVisible = (() => {
    if (!isUsingLoading) {
      return false;
    }
    if (!list) {
      return true;
    }
    if ((page?.cur || 0) < (page?.maxpage || 1) && !!getList) {
      return true;
    }
    return false;
  })();

  function getNextData() {
    const nextPage = (page?.cur || 0) + 1;
    if (loadingRef?.current) {
      return;
    }
    if (!!page && (page?.maxpage || 1) < nextPage) {
      return;
    }
    if (lastPageRef.current === nextPage) {
      return;
    }
    lastPageRef.current = nextPage;
    getList?.(nextPage);
  }

  useIntersectionObserver(listRef, {
    onIntersect: getNextData,
    threshold: 0.8,
    enabled: !loadingRef?.current && enabled,
  });

  return (
    <div
      className={styles.container}
      style={{
        ...(listData.length === 0
          ? {
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              height: "100%",
            }
          : {}),
      }}>
      {listData.length > 0 && (
        <div className={[styles.containerClass, containerClass].join(" ")}>
          {listData.map((item, index) => renderItem(item, index))}
        </div>
      )}
      <div ref={listRef} />
      {!!isLoadingVisible ? (
        <div
          className={styles.loading}
          style={{
            padding: `${loadingWidth * 0.3}px 0`,
            ...(listData.length === 0 ? {height: "100%", flex: 1} : {}),
          }}>
          <Loading width={loadingWidth} isFull={isFullLoading} />
        </div>
      ) : !!list && listData.length === 0 && !!(!!emptyText || !!emptyBtn) ? (
        <></>
      ) : undefined}
    </div>
  );
}
